<script setup>
// §8.6 ProgressToTarget — "31h 20m of 38h". A progress bar, not a status.
// Uses the interactive colour as a neutral magnitude fill (not a status hue).
// Numerals tabular (§5). Over-target is shown, never hidden.
import { computed } from 'vue'

const props = defineProps({
	value: { type: Number, required: true }, // e.g. minutes done
	target: { type: Number, required: true },
	format: { type: Function, default: (v) => String(v) },
	label: { type: String, default: null },
})
const pct = computed(() => (props.target ? Math.min((props.value / props.target) * 100, 100) : 0))
const over = computed(() => props.value > props.target)
</script>

<template>
	<div class="l-progress">
		<div class="top">
			<span v-if="label" class="lbl">{{ label }}</span>
			<span class="figures num">
				<span class="done">{{ format(value) }}</span>
				<span class="of">of {{ format(target) }}</span>
			</span>
		</div>
		<div class="track" role="progressbar" :aria-valuenow="value" :aria-valuemin="0" :aria-valuemax="target">
			<div class="fill" :class="{ over }" :style="{ width: pct + '%' }" />
		</div>
	</div>
</template>

<style scoped>
.l-progress
{
	display: flex;
	flex-direction: column;
	gap: 7px;
}

.top
{
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: var(--sp-3);
}

.lbl
{
	font-size: var(--text-chip);
	font-weight: 600;
	color: var(--text-2);
}

.figures
{
	font-size: var(--text-sm);
}

.done
{
	font-weight: 600;
	color: var(--text);
}

.of
{
	color: var(--text-3);
	margin-left: 4px;
}

.track
{
	height: 8px;
	background: var(--surface-sunken);
	border-radius: var(--r-pill);
	overflow: hidden;
}

.fill
{
	height: 100%;
	background: var(--interactive);
	border-radius: var(--r-pill);
	transition: width var(--t-fast) var(--ease);
}

.fill.over
{
	background: var(--accent-edge);
}
</style>
